/**
 * CountryAutocomplete.jsx
 *
 * Reusable Material-UI Autocomplete for ISO 2-letter country codes.
 * 266 entries, alphabetically sorted by country name.
 *
 * Behavior:
 *   - User can search by ISO code (e.g. "IN") OR country name (e.g. "India")
 *   - Selecting fires onChange(code, name)
 *   - Designed to be used standalone (not just inside a table) — accepts
 *     a `variant` prop to switch between standard (inline / no border) and
 *     outlined (form field) styles.
 *
 * Props:
 *   value       - ISO code string (e.g. "IN")
 *   onChange    - (code: string, name: string) => void
 *   label       - label text (used only when variant === "outlined")
 *   placeholder - placeholder text
 *   variant     - "standard" | "outlined" (default "outlined")
 *   required    - bool
 *   error       - bool
 *   helperText  - string
 *   fullWidth   - bool
 */

import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getAllCountries, getNameForCountryCode } from '../utils/countryLookup';

const CountryAutocomplete = ({
  value = '',
  onChange,
  label = 'Country',
  placeholder = 'Search country...',
  variant = 'outlined',
  required = false,
  error = false,
  helperText = '',
  fullWidth = true,
}) => {
  const options = useMemo(() => getAllCountries(), []);

  const currentOption = useMemo(() => {
    if (!value) return null;
    return (
      options.find((c) => c.code === value.toUpperCase()) ||
      { code: value, name: getNameForCountryCode(value) }
    );
  }, [value, options]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={currentOption}
      onChange={(_, newValue) => {
        if (newValue == null) {
          onChange?.('', '');
        } else {
          onChange?.(newValue.code, newValue.name);
        }
      }}
      getOptionLabel={(opt) => {
        if (!opt) return '';
        if (typeof opt === 'string') return opt;
        return `${opt.code} — ${opt.name}`;
      }}
      isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
      renderOption={(props, opt) => (
        <li {...props} key={opt.code} style={{ fontSize: 12 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600, marginRight: 8 }}>
            {opt.code}
          </span>
          {opt.name}
        </li>
      )}
      filterOptions={(opts, { inputValue }) => {
        if (!inputValue) return opts;
        const q = inputValue.toLowerCase();
        return opts.filter(
          (o) =>
            o.code.toLowerCase().includes(q) ||
            o.name.toLowerCase().includes(q)
        );
      }}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          label={variant === 'outlined' ? label : undefined}
          placeholder={placeholder}
          variant={variant}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={
            variant === 'standard'
              ? {
                  ...params.InputProps,
                  disableUnderline: true,
                  style: { fontSize: 10.5, padding: '0 4px' },
                }
              : params.InputProps
          }
        />
      )}
    />
  );
};

export default CountryAutocomplete;